import { useContext } from 'react'

import { articleCreationSourceLabel } from '@/common/app/modules/media/article'
import {
  articleApprovalStatusBadgeType,
  articleApprovalStatusLabel
} from '@/common/app/modules/media/articleApproval'
import { dateTimeStr } from '@/common/form/date'
import { ArticleCreationSource } from '@/common/model/media/article'
import { ArticleVersionListItem } from '@/common/model/media/article'

import Badge from '@/components/view/atoms/badge/Badge'
import TextButton from '@/components/view/atoms/button/TextButton'
import Heading from '@/components/view/atoms/heading/Heading'
import Icon, { IconType } from '@/components/view/atoms/icon/Icon'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'

import { ArticleDiffModalContext } from '@/components/container/modal/article/ArticleDiffModalProvider'
import { ActionDetailModalContext } from '@/components/container/modal/automationReview/ActionDetailModalProvider'

interface Props {
  siteId: number
  articleProposalId: number
  history: ArticleVersionListItem[]
  onChanged?: () => void
}

const ArticleUpdateHistory = ({ siteId, articleProposalId, history, onChanged }: Props) => {
  const { showModal: showArticleDiff } = useContext(ArticleDiffModalContext)
  const { showModal: showActionDetail } = useContext(ActionDetailModalContext)

  return (
    <Area>
      <Heading>更新履歴</Heading>
      <AreaContent>
        {history.map((article) => {
          const upload = article.upload
          return (
            <div key={article.id} className='border-secondary rounded-lg border px-4 py-3'>
              <div className='flex items-center gap-3'>
                <Text weight='bold' nowrap size={12}>
                  v{article.version_number}
                </Text>
                <div className='flex w-full flex-col gap-2'>
                  <div className='flex justify-between'>
                    <div className='flex items-center gap-1'>
                      {article.creation_source != ArticleCreationSource.MASTER_SYNC && (
                        <Icon type={article.ai_used ? IconType.AI_FILL : IconType.USER_SQUARE} />
                      )}
                      <Text weight='bold' size={12}>
                        {articleCreationSourceLabel(article.creation_source)}
                      </Text>
                    </div>
                    <TextButton
                      size={12}
                      onClick={() =>
                        showArticleDiff({ siteId, articleProposalId, articleId: article.id })
                      }
                    >
                      更新内容を見る
                    </TextButton>
                  </div>
                  <Text size={12} color={TextColor.SECONDARY}>
                    {article.updated_at ? dateTimeStr(article.updated_at) : '-'}
                    {article.site_operator_name ? `（${article.site_operator_name}）` : ''}
                  </Text>

                  {/* 推奨アクション（起点アクションが紐づく場合。押下で詳細モーダル） */}
                  {article.source_media_action_id != null && (
                    <div className='flex items-center gap-1'>
                      <Text size={12} color={TextColor.SECONDARY}>
                        推奨アクションID：
                      </Text>
                      <TextButton
                        size={12}
                        onClick={() =>
                          showActionDetail({
                            siteId,
                            actionId: article.source_media_action_id as number,
                            onChanged
                          })
                        }
                      >
                        AC-{article.source_media_action_id}
                      </TextButton>
                    </div>
                  )}

                  {/* 承認リクエスト（あればステータス・承認者・コメント） */}
                  {upload && (
                    <div className='flex flex-col gap-1'>
                      <div className='flex items-center gap-2'>
                        <Badge size={10} badgeType={articleApprovalStatusBadgeType(upload.status)}>
                          {articleApprovalStatusLabel(upload.status)}
                        </Badge>
                        {upload.approved_by_name && (
                          <Text size={12} color={TextColor.SECONDARY}>
                            {upload.approved_at ? dateTimeStr(upload.approved_at) : ''}（
                            {upload.approved_by_name}）
                          </Text>
                        )}
                      </div>
                      {(upload.approval_comment || upload.reject_comment) && (
                        <div className='bg-secondary rounded-lg px-3 py-2'>
                          <Text size={12} lineHeight={150}>
                            {upload.approval_comment || upload.reject_comment}
                          </Text>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </AreaContent>
    </Area>
  )
}

export default ArticleUpdateHistory
